// Tournament details interface

import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import TeamsList from './TeamsList';
import CardTemplate from './CardTemplate';
import PropItem from './PropItem';
import PropItemsBlock from './PropItemsBlock';
import TournamentCardEdit from './TournamentCardEdit';
import Tabs from './Tabs';
import Scheduler from './Scheduler';
import CompactSchedule from './CompactSchedule';
import GameList from './GameList';
import RefereeReport from './RefereeReport';
import TournamentRefereeBlock from './TournamentRefereeBlock';
import OrganizerContacts from './OrganizerContacts';
import ScheduleSource from './ScheduleSource';
import Brackets from './Brackets';
import Standings from './Standings';
import FieldManagersList from './FieldManagersList';
import { useFirebase } from './FirebaseContextProvider';
import GuidedTour from './GuidedTour';
import CheckInList from './CheckInList';
import Spinner from './Spinner';
import {
  debounce,
  formatAges,
  formatGenders,
  clearSavedTournament,
} from '../utils/misc';
import { tournamentUpdate } from '../utils/firebase';
import {
  useDivisions,
  useUserTournamentTeams,
  useTournamentParts,
} from '../utils/hooks';
import { storageScrollY, storageTournament } from '../utils/consts';
import { buildColorLegend } from '../utils/colorLegend';
import placeholderTournament from '../assets/images/placeholder-tournament.png';
import './TournamentCard.scss';
import DateRangeBadge from './DateRangeBadge';
import TournamentInfo from './TournamentInfo';
import { getBaseRoute } from './LoginCard';

const assignerTabs = new Set(['#schedule', '#referees']);

// The Teams tab is only rendered when the user has teams in this tournament,
// so its step is prepended conditionally
const teamsStep = {
  target: '.tabs-header-item-teams',
  title: 'Your Teams tab',
  content: 'Here you can view your teams participating in this tournament',
};

const steps = [
  {
    target: '.tabs-header-item-schedule',
    title: 'Schedule tab',
    content: 'Here you can view the tournament schedule',
  },
  {
    target: '.tabs-header-item-games',
    title: 'Games tab',
    content: 'Here you can view the timeline of all games',
  },
  {
    target: '.tabs-header-item-standings',
    title: 'Standings tab',
    content: 'Here you can view ranking for pool games and playoffs',
  },
  {
    target: '.tabs-header-item-brackets',
    title: 'Brackets tab',
    content: 'Here you can view playoff brackets',
  },
];

export default function TournamentCard({ tournament }) {
  const {
    id,
    imgUrl,
    imgThumbUrl,
    name,
    location,
    startDate,
    endDate,
    sports,
    genders,
    ages,
    fields,
    isSchedulePublished,
    fee,
    deposit,
    cutOffDate,
    hidden,
    source,
    admittedTeamIds,
  } = tournament;

  const {
    user,
    userIsCoach,
    userIsPlayer,
    userIsParent,
    userIsAdmin,
    userIsReferee,
    userIsRefereeAssigner,
    userManagesReferees,
  } = useFirebase();

  // Held for the whole card rather than by each tab: five of the nine tabs
  // read some of these, and registering per tab would tear a listener down and
  // reopen it on every tab switch. The two private parts stay with the tabs
  // that show them.
  const arePartsReady = useTournamentParts(id, [
    'fields',
    'games',
    'teams',
    'standings',
  ]);
  // Apart from the rest: `arePartsReady` gates the schedule tabs, which do not
  // wait on the info
  useTournamentParts(id, ['info']);
  const infoText =
    tournament.info?.find((entry) => entry.id === 'main')?.text ?? '';
  const infoSynced = tournament.infoSynced === true;
  const infoFailed = tournament.infoFailed === true;

  const divisions = useDivisions(id);
  const { teams: myTeams, isReady: myTeamsReady } = useUserTournamentTeams(id);

  useEffect(() => {
    localStorage.setItem(storageTournament, id);

    return () => clearSavedTournament();
  }, [id]);

  const handleScroll = useMemo(
    () =>
      debounce(() => {
        const scrollTop = window.scrollY;
        localStorage.setItem(storageScrollY, scrollTop);
      }, 500),
    [],
  );

  // Restore the previous scroll position when the component mounts
  useEffect(() => {
    const savedScrollY = localStorage.getItem(storageScrollY);
    if (savedScrollY)
      window.scrollTo({
        top: parseInt(savedScrollY, 10),
        left: 0,
        behavior: 'smooth',
      });
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const colorLegend = useMemo(() => buildColorLegend(divisions), [divisions]);

  const hasMyTeams = myTeams.length > 0;
  const tourSteps = useMemo(
    () => (hasMyTeams ? [teamsStep, ...steps] : steps),
    [hasMyTeams],
  );

  // An assigner creates and edits the hidden events only, in a short form
  const canEditEvent =
    userIsRefereeAssigner && (id === 'new' || hidden === true);
  const canEdit = userIsAdmin || canEditEvent;
  // An import writes only where no team was admitted, and an assigner's only
  // into a hidden event. A registration tournament never offers one
  const canImport =
    (userIsAdmin || (userIsRefereeAssigner && hidden === true)) &&
    !admittedTeamIds?.length;
  const showSource =
    canImport && (!!source || (hidden === true && !sports?.length));
  const listPath = `${getBaseRoute(user)}#tournaments`;

  const onDelete = async () => {
    try {
      await tournamentUpdate({ id, active: false });
    } catch (error) {
      toast.error(error?.message);
    }
  };

  // These four tabs all read the schedule. Since the subcollections are only
  // read while this card is open, they are not in memory on the first frame,
  // and every one of these tabs renders emptiness confidently — an empty grid,
  // "no standings yet", a bracket that is not scheduled.
  const LiveBlock = ({ children, isLive }) => {
    if (!isLive) return <p className='not-live-yet'>Not live yet</p>;
    return arePartsReady ? children : <Spinner show inline />;
  };

  // A switch would unmount the Info tab's form with its draft, so the other
  // tabs lock while it is open
  const [isInfoEditing, setIsInfoEditing] = useState(false);

  const tabs = useMemo(() => {
    const isLive = userManagesReferees || isSchedulePublished;

    const all = [
      // Admins manage every team here; coaches/players/parents get the same
      // list narrowed to their own participating teams (empty tab suppressed)
      ...(userIsAdmin ||
      ((userIsCoach || userIsPlayer || userIsParent) && myTeams.length > 0)
        ? [
            {
              title: userIsAdmin ? 'Teams' : 'Your Teams',
              hash: '#teams',
              component: <TeamsList tournamentId={id} />,
            },
          ]
        : []),
      ...(userIsAdmin
        ? [
            {
              title: 'Check in',
              hash: '#checkin',
              component: <CheckInList tournamentId={id} />,
            },
          ]
        : []),

      ...(userIsAdmin
        ? [
            {
              title: 'Managers',
              hash: '#managers',
              component: <FieldManagersList tournamentId={id} />,
            },
          ]
        : []),
      {
        title: 'Schedule',
        hash: '#schedule',
        component: (
          <LiveBlock isLive={isLive}>
            {userIsRefereeAssigner || userIsReferee ? (
              <CompactSchedule id={id} colorLegend={colorLegend} />
            ) : (
              <Scheduler id={id} colorLegend={colorLegend} />
            )}
          </LiveBlock>
        ),
      },
      {
        title: 'Games',
        hash: '#games',
        component: (
          <LiveBlock isLive={isLive}>
            <GameList id={id} colorLegend={colorLegend} />
          </LiveBlock>
        ),
      },
      // Those who manage referees always see live data, so no LiveBlock gate
      ...(userManagesReferees
        ? [
            {
              title: 'Referees',
              hash: '#referees',
              component: <RefereeReport id={id} colorLegend={colorLegend} />,
            },
          ]
        : []),
      {
        title: 'Standings',
        hash: '#standings',
        component: (
          <LiveBlock isLive={isLive}>
            <Standings id={id} colorLegend={colorLegend} />
          </LiveBlock>
        ),
      },
      {
        title: 'Brackets',
        hash: '#brackets',
        component: (
          <LiveBlock isLive={isLive}>
            <Brackets id={id} fields={fields} />
          </LiveBlock>
        ),
      },
      // Others only get it once there is something to read
      ...(userIsAdmin || infoText
        ? [
            {
              title: 'Info',
              hash: '#info',
              // Keyed: the card outlives a switch to another tournament, and an
              // open form would save its text into that one
              component: (
                <TournamentInfo
                  key={id}
                  id={id}
                  text={infoText}
                  isSynced={infoSynced}
                  isFailed={infoFailed}
                  canEdit={userIsAdmin}
                  onEditingChange={setIsInfoEditing}
                />
              ),
            },
          ]
        : []),
    ];
    // An assigner gets the schedule, to assign referees, and their pay
    const shown = userIsRefereeAssigner
      ? all.filter((tab) => assignerTabs.has(tab.hash))
      : all;
    return shown.map((tab) =>
      isInfoEditing && tab.hash !== '#info' ? { ...tab, disabled: true } : tab,
    );
  }, [
    id,
    colorLegend,
    fields,
    // LiveBlock closes over it
    arePartsReady,
    userIsAdmin,
    userIsReferee,
    userIsRefereeAssigner,
    userManagesReferees,
    userIsCoach,
    userIsPlayer,
    userIsParent,
    myTeams.length,
    isSchedulePublished,
    infoText,
    infoSynced,
    infoFailed,
    isInfoEditing,
  ]);

  return (
    <GuidedTour
      steps={userIsCoach || userIsPlayer ? tourSteps : undefined}
      // Wait for the teams to load, otherwise the tour starts without the Teams
      // step and the tab it points at appears a moment later
      run={myTeamsReady}
      // One key for the whole card, whether or not the Teams step is in: the key
      // records that this tour was seen, so tying it to the teams would replay
      // the tour the day a coach's team is invited
      tourKey='tournament-card-v2'
    >
      <CardTemplate
        header='Tournament'
        className='tournament-card'
        backBtn
        imgUrl={imgUrl}
        imgThumbUrl={imgThumbUrl}
        imgTitle={name}
        name={name}
        placeholder={placeholderTournament}
        nameCompanion={<DateRangeBadge start={startDate} end={endDate} />}
        aside={<OrganizerContacts org={tournament.organizations?.[0]} />}
        details={
          <PropItemsBlock>
            <PropItem prop='Location' value={location} />
            {showSource && <ScheduleSource id={id} source={source} />}
            {/* An event nobody registers for has no terms to show */}
            {sports?.length > 0 && (
              <>
                <PropItem prop='Sport' value={sports.join(', ')} />
                <PropItem
                  prop='Division'
                  value={`${formatGenders(genders)}, ${formatAges(ages)}`}
                />
                <PropItem prop='Cut-off date' value={cutOffDate} />
                {(userIsCoach || userIsAdmin) && (
                  <PropItem
                    prop='Fee'
                    value={'$' + fee + ' / $' + (deposit ?? 0)}
                  />
                )}
              </>
            )}
          </PropItemsBlock>
        }
        onDelete={canEdit && onDelete}
        afterDeleteNavPath={listPath}
        editForm={
          canEdit && (
            <TournamentCardEdit
              tournament={tournament}
              teamless={!userIsAdmin}
              afterCreateNavPath={listPath}
            />
          )
        }
      >
        {userIsReferee && <TournamentRefereeBlock id={id} />}
        <Tabs className='tournament-card-tabs' tabs={tabs} />
      </CardTemplate>
    </GuidedTour>
  );
}
